import { useEffect, useState } from 'react';

import { getAuth, sendPasswordResetEmail } from 'firebase/auth';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import EmailIcon from '@mui/icons-material/Email';
import PasswordIcon from '@mui/icons-material/Password';

import GratiPageWrapper from '../components/common/GratiPageWrapper';
import { useApp } from '../contextProviders/AppProvider';
import { useAuth } from '../contextProviders/AuthProvider';
import { useRequestInviteByEmail } from '../dataMutators/useRequestInviteByEmail';

export default function AccountForgotPasswordPage() {
  const { authUrl } = useAuth();
  const { setPageName } = useApp();
  const [email, setEmail] = useState('');
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { requestInviteByEmail } = useRequestInviteByEmail();

  useEffect(() => {
    setPageName('Forgot Password');
  }, [setPageName]);

  const handleSubmit = async () => {
    setErrorMessage(null);
    const auth = getAuth();
    const continueUrl = `${authUrl}/account-reset-password`;
    const url = `${authUrl}?continueUrl=${encodeURIComponent(continueUrl)}`;

    // Someone with a pending invitation has no account to reset, so also email their invitation link
    const [resetResult] = await Promise.allSettled([
      sendPasswordResetEmail(auth, email, { url, handleCodeInApp: true }),
      requestInviteByEmail(email),
    ]);

    if (resetResult.status === 'fulfilled') {
      setIsEmailSent(true);
      return;
    }
    const error = resetResult.reason as { code?: string };
    console.error('Full error:', error);
    if (error.code === 'auth/user-not-found') {
      // No account, but there may be an invitation — same message either way
      setIsEmailSent(true);
    } else if (error.code === 'auth/too-many-requests') {
      setErrorMessage('Too many attempts. Please try again later.');
    } else if (error.code === 'auth/invalid-email') {
      setErrorMessage('Please enter a valid email address.');
    } else {
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  return (
    <GratiPageWrapper isNoAuthRequired={true} isAuthRequired={false}>
      <>
        {isEmailSent ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <Paper
              elevation={3}
              sx={{
                p: 4,
                maxWidth: 400,
                width: '100%',
                textAlign: 'center',
                backgroundColor: 'background.paper',
              }}
            >
              <Stack spacing={3} sx={{
                alignItems: "center"
              }}>
                <EmailIcon color="primary" sx={{ fontSize: 48 }} />
                <Typography variant="titleMedium" gutterBottom>
                  Check Your Email
                </Typography>
                <Typography variant="bodyLarge" sx={{
                  color: "text.secondary"
                }}>
                  If there&apos;s an account or invitation for this email, we&apos;ve sent you a link:
                </Typography>
                <Typography variant="bodyLarge" sx={{
                  fontWeight: "bold"
                }}>
                  {email}
                </Typography>
                <Typography variant="bodyMedium" sx={{
                  color: "text.secondary"
                }}>
                  Tap the link in the email to continue. If you don&apos;t see the email, check your spam folder.
                </Typography>
              </Stack>
            </Paper>
          </Box>
        ) : (
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <Stack spacing={2} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="titleMedium">Forgot your password?</Typography>
              <Typography variant="bodyMedium" sx={{
                color: "text.secondary"
              }}>
                Enter your email address and we&apos;ll email you a link to reset your password.
              </Typography>
              <TextField
                fullWidth
                required
                id="email"
                label="email"
                aria-label="email"
                placeholder="name@email.com"
                helperText="Email address to reset password"
                onChange={({ target }) => setEmail(target.value.toLowerCase())}
              />
              <Button type="submit" variant="contained" startIcon={<PasswordIcon />}>
                Reset password
              </Button>
              {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            </Stack>
          </Box>
        )}
      </>
    </GratiPageWrapper>
  );
}
